import type { CombatTarget } from './combat';

/**
 * Tap to target (docs/WEAPONS.md): a finger on a person or a car in the street, while armed, makes it
 * the lock. Pure: the view projects the candidates to the screen and asks `pickTapTarget`; the tap
 * gesture itself is told apart from a drag or a hold by `isTap`.
 */

/** A lock candidate as drawn on the screen (CSS px). */
export interface TapCandidate {
  kind: CombatTarget['kind'];
  id: number;
  /** Screen position of the body's middle. */
  sx: number;
  sy: number;
  /** The body's radius on the screen (px): a person is a few px, a car its half-length. */
  rPx: number;
}

/** A fingertip is fat: a person within this many px of the tap (beyond their body) counts as tapped. */
export const TAP_SLOP_PX = 30;
/** A tap: down and up within this long (ms)… */
export const TAP_MAX_MS = 350;
/** …and within this far (px). Further is a drag; longer is a hold. */
export const TAP_MAX_MOVE_PX = 14;

export function isTap(ms: number, dx: number, dy: number): boolean {
  return ms >= 0 && ms <= TAP_MAX_MS && Math.hypot(dx, dy) <= TAP_MAX_MOVE_PX;
}

/** A flick (swipe to switch target): down and up within this long (ms)… */
export const FLICK_MAX_MS = 300;
/** …having travelled at least this far (px). Slower or shorter is a tap or a drag. */
export const FLICK_MIN_PX = 40;
/** Half-angle (rad) around the flick's direction a candidate must be in to be the next lock (~50°). */
export const FLICK_CONE = 0.87;

export function isFlick(ms: number, dx: number, dy: number): boolean {
  return ms >= 0 && ms <= FLICK_MAX_MS && Math.hypot(dx, dy) >= FLICK_MIN_PX;
}

/**
 * Swipe to switch: the next lock after a flick (dx, dy in screen px) from the current lock's spot (ox, oy;
 * the player's own spot with no lock). The nearest candidate inside FLICK_CONE of the flick's direction,
 * the direction counting for more than the distance (a man 45° off the line costs ×3: the flick says
 * which way, not who is closest), people before cars, and never the one we are on. Null = nobody that
 * way (the lock is left as it is).
 */
const FLICK_OFF_LINE_COST = 2.5;
export function pickSwipeTarget(ox: number, oy: number, dx: number, dy: number, candidates: readonly TapCandidate[], current: { kind: string; id: number } | null): TapCandidate | null {
  const l = Math.hypot(dx, dy);
  if (l < 1e-6) return null;
  const ux = dx / l;
  const uy = dy / l;
  let best: TapCandidate | null = null;
  let bestScore = Infinity;
  for (const c of candidates) {
    if (current && c.kind === current.kind && c.id === current.id) continue;
    const vx = c.sx - ox;
    const vy = c.sy - oy;
    const d = Math.hypot(vx, vy);
    if (d < 1) continue;
    const off = Math.acos(Math.max(-1, Math.min(1, (vx * ux + vy * uy) / d)));
    if (off > FLICK_CONE) continue;
    const score = d * (1 + FLICK_OFF_LINE_COST * off) + (c.kind === 'vehicle' ? 1e5 : 0);
    if (score < bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

/**
 * Who a tap at (x, y) means. People win: the nearest person whose body (plus the finger's slop) is
 * under the tap, measured from the body's edge. A car only when no person is: the nearest car the tap
 * landed on (within its body, a little slop). Null = empty street (the caller clears a chosen lock).
 */
export function pickTapTarget(x: number, y: number, candidates: readonly TapCandidate[], slop = TAP_SLOP_PX): TapCandidate | null {
  let person: TapCandidate | null = null;
  let personD = Infinity;
  let car: TapCandidate | null = null;
  let carD = Infinity;
  for (const c of candidates) {
    const d = Math.hypot(c.sx - x, c.sy - y);
    if (c.kind === 'vehicle') {
      if (d > c.rPx + slop * 0.3) continue;
      if (d < carD) {
        carD = d;
        car = c;
      }
      continue;
    }
    const edge = d - c.rPx;
    if (edge > slop) continue;
    if (edge < personD) {
      personD = edge;
      person = c;
    }
  }
  return person ?? car;
}
